import * as THREE from 'three';
import { Pass, makeRT, resizeRT } from './pass.js';

/**
 * Physically-plausible bloom: one bright-pass with a soft knee, a progressive
 * downsample chain and a tent-filtered additive upsample back up it.
 *
 * The downsample is the 13-tap "dual filter" from Jimenez' SIGGRAPH 2014 talk,
 * with a Karis luminance average on the first (brightest) level so a single
 * blown-out spark cannot flicker the whole halo. The upsample is a 9-tap tent
 * — the smooth, wide, halo-free spread that makes sun discs and musou aura
 * glow instead of smear.
 */

const DOWN_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tSrc;
  uniform vec2 uTexel;      // 1/srcSize
  uniform vec4 uThresh;     // x threshold, y knee, z 1/(4*knee), w intensity
  varying vec2 vUv;

  #ifdef BRIGHT_PASS
  float karis(vec3 c) { return 1.0 / (1.0 + max(c.r, max(c.g, c.b))); }
  vec3 prefilter(vec3 c) {
    float br = max(c.r, max(c.g, c.b));
    float rq = clamp(br - uThresh.x + uThresh.y, 0.0, 2.0 * uThresh.y);
    rq = rq * rq * uThresh.z;
    return c * max(rq, br - uThresh.x) / max(br, 1e-4);
  }
  #endif

  void main() {
    vec2 t = uTexel;
    vec3 a = texture2D(tSrc, vUv + t * vec2(-2.0,  2.0)).rgb;
    vec3 b = texture2D(tSrc, vUv + t * vec2( 0.0,  2.0)).rgb;
    vec3 c = texture2D(tSrc, vUv + t * vec2( 2.0,  2.0)).rgb;
    vec3 d = texture2D(tSrc, vUv + t * vec2(-2.0,  0.0)).rgb;
    vec3 e = texture2D(tSrc, vUv).rgb;
    vec3 f = texture2D(tSrc, vUv + t * vec2( 2.0,  0.0)).rgb;
    vec3 g = texture2D(tSrc, vUv + t * vec2(-2.0, -2.0)).rgb;
    vec3 h = texture2D(tSrc, vUv + t * vec2( 0.0, -2.0)).rgb;
    vec3 i = texture2D(tSrc, vUv + t * vec2( 2.0, -2.0)).rgb;
    vec3 j = texture2D(tSrc, vUv + t * vec2(-1.0,  1.0)).rgb;
    vec3 k = texture2D(tSrc, vUv + t * vec2( 1.0,  1.0)).rgb;
    vec3 l = texture2D(tSrc, vUv + t * vec2(-1.0, -1.0)).rgb;
    vec3 m = texture2D(tSrc, vUv + t * vec2( 1.0, -1.0)).rgb;

    #ifdef BRIGHT_PASS
      // group, karis-weight, then average: kills fireflies before they spread
      vec3 g0 = (a + b + d + e) * 0.25;
      vec3 g1 = (b + c + e + f) * 0.25;
      vec3 g2 = (d + e + g + h) * 0.25;
      vec3 g3 = (e + f + h + i) * 0.25;
      vec3 g4 = (j + k + l + m) * 0.25;
      float w0 = karis(g0), w1 = karis(g1), w2 = karis(g2), w3 = karis(g3), w4 = karis(g4);
      float ws = w0 * 0.125 + w1 * 0.125 + w2 * 0.125 + w3 * 0.125 + w4 * 0.5;
      vec3 col = (g0 * w0 * 0.125 + g1 * w1 * 0.125 + g2 * w2 * 0.125 + g3 * w3 * 0.125 + g4 * w4 * 0.5) / max(ws, 1e-5);
      col = prefilter(max(col, vec3(0.0)));
      gl_FragColor = vec4(col * uThresh.w, 1.0);
    #else
      vec3 col = e * 0.125;
      col += (a + c + g + i) * 0.03125;
      col += (b + d + f + h) * 0.0625;
      col += (j + k + l + m) * 0.125;
      gl_FragColor = vec4(col, 1.0);
    #endif
  }`;

const UP_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tSrc;
  uniform vec2 uTexel;     // 1/srcSize
  uniform float uRadius;
  varying vec2 vUv;
  void main() {
    vec2 t = uTexel * uRadius;
    vec3 s = texture2D(tSrc, vUv + vec2(-t.x,  t.y)).rgb;
    s += texture2D(tSrc, vUv + vec2( 0.0,  t.y)).rgb * 2.0;
    s += texture2D(tSrc, vUv + vec2( t.x,  t.y)).rgb;
    s += texture2D(tSrc, vUv + vec2(-t.x,  0.0)).rgb * 2.0;
    s += texture2D(tSrc, vUv).rgb * 4.0;
    s += texture2D(tSrc, vUv + vec2( t.x,  0.0)).rgb * 2.0;
    s += texture2D(tSrc, vUv + vec2(-t.x, -t.y)).rgb;
    s += texture2D(tSrc, vUv + vec2( 0.0, -t.y)).rgb * 2.0;
    s += texture2D(tSrc, vUv + vec2( t.x, -t.y)).rgb;
    gl_FragColor = vec4(s * (1.0 / 16.0), 1.0);
  }`;

export class Bloom {
  constructor(composer, levels = 5) {
    this.composer = composer;
    this.levels = levels;
    this.mips = [];
    // A low threshold turns bloom into a veiling glare that erases the very
    // thing it is supposed to be blooming — the sun ends up a soft skirt with
    // no disc in it. Bite later, a little harder.
    this.threshold = 2.05;
    this.knee = 0.40;
    this.intensity = 1.05;
    this.radius = 1.0;

    this.bright = new Pass(DOWN_FRAG, {
      tSrc: { value: null }, uTexel: { value: new THREE.Vector2() },
      uThresh: { value: new THREE.Vector4() },
    }, { defines: { BRIGHT_PASS: '' } });

    this.down = new Pass(DOWN_FRAG, {
      tSrc: { value: null }, uTexel: { value: new THREE.Vector2() },
      uThresh: { value: new THREE.Vector4() },
    });

    this.up = new Pass(UP_FRAG, {
      tSrc: { value: null }, uTexel: { value: new THREE.Vector2() },
      uRadius: { value: 1.0 },
    }, { blending: THREE.AdditiveBlending, transparent: true });
  }

  setSize(w, h) {
    let cw = w, ch = h;
    for (let i = 0; i < this.levels; i++) {
      cw = Math.max(2, cw >> 1); ch = Math.max(2, ch >> 1);
      if (!this.mips[i]) this.mips[i] = makeRT(cw, ch, { name: 'bloom' + i });
      else resizeRT(this.mips[i], cw, ch);
    }
    this._w = w; this._h = h;
  }

  /** @returns {THREE.Texture} half-resolution bloom to add in the composite. */
  render(srcTexture, srcW, srcH) {
    const C = this.composer;
    const kn = Math.max(1e-4, this.knee);
    this.bright.uniforms.tSrc.value = srcTexture;
    this.bright.uniforms.uTexel.value.set(1 / srcW, 1 / srcH);
    this.bright.uniforms.uThresh.value.set(this.threshold, kn, 1 / (4 * kn), this.intensity);
    C.run(this.bright, this.mips[0]);

    for (let i = 1; i < this.levels; i++) {
      const src = this.mips[i - 1];
      this.down.uniforms.tSrc.value = src.texture;
      this.down.uniforms.uTexel.value.set(1 / src.width, 1 / src.height);
      C.run(this.down, this.mips[i]);
    }
    this.up.uniforms.uRadius.value = this.radius;
    for (let i = this.levels - 1; i > 0; i--) {
      const src = this.mips[i];
      this.up.uniforms.tSrc.value = src.texture;
      this.up.uniforms.uTexel.value.set(1 / src.width, 1 / src.height);
      C.run(this.up, this.mips[i - 1]);
    }
    return this.mips[0].texture;
  }

  dispose() {
    for (const m of this.mips) m.dispose();
    this.bright.dispose(); this.down.dispose(); this.up.dispose();
  }
}
